import { useMemo } from 'react';
import { useRouteLoaderData } from '@remix-run/react';
import type { FeedEntry } from '~/services/layout';
import type { UseResponsiveBreakpoint } from '~/hooks/use-responsive-breakpoint';
import type { loader as rootLoader } from '~/root.tsx';
import {
  ENTRY_ACTIONS_IDENTIFIER,
  resolvePopupMenuActionItems,
} from '../utils/resolve-popup-menu-actions';
import { buildPowerCellRightClickContextualEntry } from '../utils/contextual-action-policy';
import { isPopupMenuActionIdentifier } from '../utils/popup-menu-action-identifier';

export type PopupMenuActionsSource = 'configured' | 'entry';

export type PopupMenuAvailability = {
  /** which actions a right-click on the cell shows */
  contextMenuActionsSource: PopupMenuActionsSource;
  /** the cell action icon opens the configured menu */
  canOpenFromActionIcon: boolean;
  /** a right-click on the cell opens a menu */
  canOpenFromContextMenu: boolean;
  /** buttons-container `popup_menu` buttons open the configured menu */
  canOpenFromButtons: boolean;
  /** the action icon renders (a popup_menu icon only with something to show) */
  shouldRenderActionIcon: boolean;
};

export type GetPopupMenuAvailabilityArgs = {
  entry: FeedEntry;
  actionIdentifier: string | undefined;
  popupMenuActionsConfig: string;
  favoritesAvailable: boolean;
  responsiveBreakpoint: UseResponsiveBreakpoint;
};

export type UsePopupMenuAvailabilityArgs = Omit<
  GetPopupMenuAvailabilityArgs,
  'popupMenuActionsConfig'
>;

const CONTEXT_MENU_BREAKPOINTS: ReadonlySet<UseResponsiveBreakpoint> =
  new Set<UseResponsiveBreakpoint>(['desktop', 'large-desktop']);

/**
 * Decides, before render, which Popup Triggers of a cell open a Popup Menu.
 * @returns the cell's Popup Menu Availability
 */
export function getPopupMenuAvailability({
  entry,
  actionIdentifier,
  popupMenuActionsConfig,
  favoritesAvailable,
  responsiveBreakpoint,
}: GetPopupMenuAvailabilityArgs): PopupMenuAvailability {
  const isPopupMenuAction: boolean =
    isPopupMenuActionIdentifier(actionIdentifier);

  const hasConfiguredActions: boolean = resolvePopupMenuActionItems({
    identifiers: popupMenuActionsConfig,
    entry,
    favoritesAvailable,
  }).hasRenderableItems;

  const hasEntryActions: boolean = buildPowerCellRightClickContextualEntry({
    entry,
  }).hasRenderableActions;

  const hasContextMenuActions: boolean = isPopupMenuAction
    ? hasConfiguredActions
    : hasEntryActions;

  return {
    contextMenuActionsSource: isPopupMenuAction ? 'configured' : 'entry',
    canOpenFromActionIcon: isPopupMenuAction && hasConfiguredActions,
    canOpenFromContextMenu:
      hasContextMenuActions &&
      CONTEXT_MENU_BREAKPOINTS.has(responsiveBreakpoint),
    canOpenFromButtons: hasConfiguredActions,
    shouldRenderActionIcon: !isPopupMenuAction || hasConfiguredActions,
  };
}

/**
 * Cell-facing wrapper of `getPopupMenuAvailability` reading the configured
 * actions list from the root loader data.
 * @returns the cell's Popup Menu Availability
 */
export function usePopupMenuAvailability({
  entry,
  actionIdentifier,
  favoritesAvailable,
  responsiveBreakpoint,
}: UsePopupMenuAvailabilityArgs): PopupMenuAvailability {
  const rootLoaderData = useRouteLoaderData<typeof rootLoader>('root');

  const popupMenuActionsConfig: string =
    rootLoaderData?.popupMenuConfig?.customConfiguration?.popup_menu_actions ||
    ENTRY_ACTIONS_IDENTIFIER;

  return useMemo(
    () =>
      getPopupMenuAvailability({
        entry,
        actionIdentifier,
        popupMenuActionsConfig,
        favoritesAvailable,
        responsiveBreakpoint,
      }),
    [
      entry,
      actionIdentifier,
      popupMenuActionsConfig,
      favoritesAvailable,
      responsiveBreakpoint,
    ]
  );
}
